import { useEffect, useState } from 'react';
import { SubCategoryButton } from '../HighlightedCategories/subCategoryButton';
import { ActiveFilter, SearchFilter } from 'types/Search';
import { TopFiltersCard } from '../TopFiltersCard/TopFiltersCard';
import {
  SUBCATEGORIES,
  SearchTopCategory,
  SubCategoryValue
} from './SearchTopCategoriesModel';
import { Box } from '@mui/material';
import { INCOS_EVENTS } from 'components/constants/Constants';

const INCOS_FILTERS_CONTAINER_ID = 'incos-filters-container';

interface SearchCategoriesBannersProps {
  updateFilters: (activeFilters: ActiveFilter[]) => void;
  activeFilters: SearchFilter[];
}

const SearchHighlightedCategories = ({
  updateFilters,
  activeFilters
}: SearchCategoriesBannersProps) => {
  // Needs to have its own state because we can open a card without changing the filters
  //   and this information is not stored in the parent component.
  const [selectedCategory, setSelectedCategory] = useState<SearchTopCategory>();

  useEffect(() => {
    // open a category if there are active filters for it
    if (activeFilters.length > 0) {
      const category = Object.entries(SUBCATEGORIES).find(
        ([, categoryValues]) =>
          categoryValues.some(categoryValue =>
            activeFilters.find(
              f =>
                f.field === categoryValue.filter &&
                f.context === categoryValue.context
            )
          )
      )?.[0] as SearchTopCategory;

      if (category) {
        setSelectedCategory(category);
      }
    }
  }, [activeFilters]);

  const resetAllFilters = () => {
    updateFilters(
      Object.values(SUBCATEGORIES)
        .flatMap(c => c)
        .map(c => ({ property: c.filter, value: [] }))
    );
  };

  const handleSearchCategoryButtonClick = (
    selected: boolean,
    subCategoryValue: SubCategoryValue
  ) => {
    const searchFiltersFromActiveFilters = activeFilters.filter(
      f =>
        f.field === subCategoryValue.filter &&
        f.context === subCategoryValue.context
    );
    updateFilters([
      {
        property: subCategoryValue.filter,
        value: selected
          ? searchFiltersFromActiveFilters.filter(
              f =>
                f.value !==
                (f.field === 'events'
                  ? mapIncosEventToEventkey(subCategoryValue.value)
                  : subCategoryValue.value)
            )
          : //we just want to allow the user to select one event filter
          subCategoryValue.filter !== 'events'
          ? [
              ...searchFiltersFromActiveFilters,
              {
                value: subCategoryValue.value,
                context: subCategoryValue.context,
                label: subCategoryValue.label
              }
            ]
          : [
              {
                value: mapIncosEventToEventkey(subCategoryValue.value),
                label: subCategoryValue.label
              }
            ]
      }
    ]);
  };

  return (
    <div id={INCOS_FILTERS_CONTAINER_ID}>
      <Box className="categories-wrapper" sx={{ mt: { xs: 1, md: 2 } }}>
        <Box
          className="awards-cards-container "
          sx={{ mb: { xs: 1, md: 2 }, mx: 2, gap: { xs: 0.5, sm: 3 } }}
        >
          {[
            SearchTopCategory.EVENTS,
            SearchTopCategory.INNOVATION_ZONE,
            SearchTopCategory.AWARDS,
            SearchTopCategory.TRENDS
          ].map(category => {
            const isSelected = selectedCategory === category;

            return (
              <TopFiltersCard
                key={category}
                category={category}
                isSelected={isSelected}
                onClick={() => {
                  setSelectedCategory(isSelected ? undefined : category);
                  if (selectedCategory !== undefined) {
                    // only reset if one of the categories is already open
                    resetAllFilters();
                  }
                }}
              />
            );
          })}
        </Box>
      </Box>
      {selectedCategory && (
        <Box sx={{ marginX: 2 }}>
          <Box sx={{ overflowX: 'auto', display: 'flex', gap: 2 }}>
            {SUBCATEGORIES[selectedCategory].map(sub => {
              const selected = isSubCategoryButtonSelected(
                sub.filter,
                sub.value,
                sub.context,
                activeFilters
              );
              return (
                <SubCategoryButton
                  key={sub.value}
                  onClick={() => handleSearchCategoryButtonClick(selected, sub)}
                  activeFilters={activeFilters}
                  selected={selected}
                  label={sub.label}
                  separator={sub.extra === 'separator'}
                />
              );
            })}
          </Box>
        </Box>
      )}
    </div>
  );
};

const isSubCategoryButtonSelected = (
  filter: string,
  value: string,
  context: string | undefined,
  activeFilters: SearchFilter[]
) =>
  activeFilters.some(
    f =>
      f.field === filter &&
      f.value ===
        (filter === 'events' ? mapIncosEventToEventkey(value) : value) &&
      f.context === context
  );

const mapIncosEventToEventkey = (value: string) =>
  INCOS_EVENTS.find((el: { key: string }) => el.key === value)?.eventKey;

export default SearchHighlightedCategories;
